import type { AxiosError } from "axios";

export interface ParsedApiError {
  code: string;
  message: string;
  status: number;
  requestId?: string;
  fieldErrors?: { field: string; message: string }[];
  resourceType?: string;
  resourceId?: string;
}

export type ErrorContext = "form" | "page-load" | "mutation" | "background";

export type ErrorRenderingType =
  | "session-modal"
  | "permission-denied"
  | "not-found"
  | "full-page-error"
  | "network-error"
  | "form-fields"
  | "form-alert"
  | "toast"
  | "silent";

export interface ErrorRendering {
  type: ErrorRenderingType;
  message: string;
  fieldErrors?: { field: string; message: string }[];
  resourceType?: string;
  retryable?: boolean;
}

const AUTH_ERROR_CODES = new Set([
  "UNAUTHORIZED",
  "INVALID_TOKEN",
  "TOKEN_EXPIRED",
]);

export function parseApiError(error: unknown): ParsedApiError {
  if (isAxiosError(error) && error.response) {
    const data = error.response.data as Record<string, unknown> | undefined;
    return {
      code: (data?.error as string) ?? "UNKNOWN_ERROR",
      message: (data?.message as string) ?? "Something went wrong. Please try again.",
      status: error.response.status,
      requestId: data?.requestId as string | undefined,
      fieldErrors: data?.fieldErrors as { field: string; message: string }[] | undefined,
      resourceType: data?.resourceType as string | undefined,
      resourceId: data?.resourceId as string | undefined,
    };
  }
  if (isAxiosError(error) && !error.response) {
    return {
      code: "NETWORK_ERROR",
      message: "No connection. Check your internet and try again.",
      status: 0,
    };
  }
  return {
    code: "UNKNOWN_ERROR",
    message: "Something went wrong. Please try again.",
    status: 0,
  };
}

export function routeError(error: ParsedApiError, context: ErrorContext): ErrorRendering {
  if (AUTH_ERROR_CODES.has(error.code) || error.status === 401) {
    return { type: "session-modal", message: "Your session has expired. Log in to continue." };
  }
  if (error.code === "NETWORK_ERROR" || error.status === 0) {
    if (context === "background") return { type: "silent", message: error.message };
    return { type: "network-error", message: error.message, retryable: true };
  }
  if (error.status === 403) {
    if (context === "page-load") return { type: "permission-denied", message: error.message };
    return { type: "toast", message: error.message };
  }
  if (error.status === 404) {
    if (context === "page-load") return { type: "not-found", message: error.message, resourceType: error.resourceType };
    return { type: "toast", message: error.message };
  }
  if (error.status === 400 && error.fieldErrors?.length && context === "form") {
    return { type: "form-fields", message: error.message, fieldErrors: error.fieldErrors };
  }
  if (error.status >= 400 && error.status < 500) {
    if (context === "form") return { type: "form-alert", message: error.message };
    if (context === "page-load") return { type: "full-page-error", message: error.message, retryable: false };
    return { type: "toast", message: error.message };
  }
  if (error.status >= 500) {
    const msg = "Something went wrong. Please try again.";
    if (context === "page-load") return { type: "full-page-error", message: msg, retryable: true };
    if (context === "background") return { type: "silent", message: msg };
    return { type: "toast", message: msg };
  }
  if (context === "background") return { type: "silent", message: error.message };
  return { type: "toast", message: error.message };
}

function isAxiosError(error: unknown): error is AxiosError {
  return typeof error === "object" && error !== null && "isAxiosError" in error && (error as AxiosError).isAxiosError === true;
}
